import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import VirtualizedDropdown, { VirtualizedDropdownOption } from '../common/VirtualizedDropdown';
import { Button } from '@ohif/ui-next';
import type { LinkedHubFacilityDetail } from '../../types/facility.types';
import type { SelectedStudy } from '../../utils/studyUtils';
import { batchAssignStudiesToHub, batchHubRoutingStudies } from '../../utils/studyAssignmentUtils';
import {
  formatPriorityDisplay,
  priorityUsesUrgentPill,
} from '../worklist/imagingWorklistTableUtils';

export interface AssignHubFacilityModalProps {
  checkedStudyIds: string[];
  selectedStudies?: SelectedStudy[];
  linkedHubFacilities: LinkedHubFacilityDetail[];
  mode?: 'assign' | 'reassign';
  onClose: () => void;
  onSuccess?: () => void;
  servicesManager?: {
    services?: {
      uiNotificationService?: {
        show: (options: {
          title: string;
          message: string;
          type: 'success' | 'error' | 'warning' | 'info';
          duration?: number;
          allowDuplicates?: boolean;
        }) => void;
      };
    };
  };
}

const AssignHubFacilityModal: React.FC<AssignHubFacilityModalProps> = ({
  checkedStudyIds,
  selectedStudies = [],
  linkedHubFacilities,
  mode = 'assign',
  onClose,
  onSuccess,
  servicesManager,
}) => {
  const { t } = useTranslation();
  const isReassign = mode === 'reassign';
  const [selectedHubId, setSelectedHubId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);

  const uiNotificationService = servicesManager?.services?.uiNotificationService;

  const studiesForDisplay = useMemo(() => {
    if (!checkedStudyIds.length) {
      return selectedStudies;
    }
    return checkedStudyIds
      .map(uid => selectedStudies.find(s => s.studyInstanceUid === uid))
      .filter((s): s is SelectedStudy => Boolean(s));
  }, [checkedStudyIds, selectedStudies]);

  const hubOptions: VirtualizedDropdownOption[] = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return linkedHubFacilities
      .filter(hub => !query || hub.name.toLowerCase().includes(query))
      .map(hub => ({
        id: hub.id,
        label: hub.name,
      }));
  }, [linkedHubFacilities, searchQuery]);

  const handleHubChange = (hubId: number | null, label: string) => {
    setSelectedHubId(hubId);
    if (hubId != null && label) {
      setSearchQuery(label);
    }
  };

  const notifyAssignResult = (
    succeeded: number,
    failed: number,
    messages: {
      successMessage: string;
      partialMessage: string;
      failureFallback: string;
      failedErrors: Array<{ error?: string }>;
    }
  ) => {
    if (!uiNotificationService) {
      return;
    }
    if (failed === 0) {
      uiNotificationService.show({
        title: t('Success'),
        message: messages.successMessage,
        type: 'success',
        duration: 5000,
      });
      return;
    }
    if (succeeded > 0) {
      uiNotificationService.show({
        title: t('Partial Success'),
        message: messages.partialMessage,
        type: 'warning',
        duration: 5000,
      });
      return;
    }
    throw new Error(messages.failedErrors[0]?.error || messages.failureFallback);
  };

  const buildHubAssignMessages = (succeeded: number, failed: number) => ({
    successMessage: isReassign
      ? t('Successfully reassigned {{count}} study(ies) to hub facility', { count: succeeded })
      : t('Successfully assigned {{count}} study(ies) to hub facility', { count: succeeded }),
    partialMessage: isReassign
      ? t('Reassigned {{success}} study(ies); {{failed}} failed', {
          success: succeeded,
          failed,
        })
      : t('Assigned {{success}} study(ies); {{failed}} failed', {
          success: succeeded,
          failed,
        }),
    failureFallback: isReassign
      ? t('Failed to reassign studies to hub facility')
      : t('Failed to assign studies to hub facility'),
  });

  const showHubAssignError = (err: unknown) => {
    const fallback = isReassign
      ? t('Failed to reassign studies to hub facility')
      : t('Failed to assign studies to hub facility');
    const errorMessage = err instanceof Error ? err.message : fallback;
    if (!uiNotificationService) {
      return;
    }
    uiNotificationService.show({
      title: t('Assignment Failed'),
      message: errorMessage,
      type: 'error',
      duration: 5000,
    });
  };

  const runHubAssignRequest = async (
    hubId: number,
    studyPayload: Array<{ study_instance_uid: string }>
  ) => {
    if (isReassign) {
      return batchHubRoutingStudies({
        action: 'reassign-hub',
        hub_facility_id: hubId,
        studies: studyPayload,
      });
    }
    return batchAssignStudiesToHub({
      hub_facility_id: hubId,
      studies: studyPayload,
    });
  };

  const handleAssign = async () => {
    if (selectedHubId == null) {
      return;
    }

    const studiesToAssign = studiesForDisplay.filter(study => study.studyInstanceUid?.trim());
    if (studiesToAssign.length === 0) {
      if (uiNotificationService) {
        uiNotificationService.show({
          title: t('Validation Error'),
          message: t('Selected studies cannot be assigned to a hub facility'),
          type: 'error',
          duration: 5000,
        });
      }
      return;
    }

    setIsAssigning(true);
    try {
      const studyPayload = studiesToAssign.map(study => ({
        study_instance_uid: study.studyInstanceUid,
      }));
      const result = await runHubAssignRequest(selectedHubId, studyPayload);
      const succeeded = result.success.length;
      const failed = result.failed.length;
      notifyAssignResult(succeeded, failed, {
        ...buildHubAssignMessages(succeeded, failed),
        failedErrors: result.failed,
      });
      onSuccess?.();
      onClose();
    } catch (err) {
      showHubAssignError(err);
    } finally {
      setIsAssigning(false);
    }
  };

  const studyCount = studiesForDisplay.length || checkedStudyIds.length;

  const priorityBadgeLabel = (study: SelectedStudy) => {
    if (study.isCritical) {
      return t('Critical');
    }
    const formatted = formatPriorityDisplay(study.priority);
    if (formatted) {
      return formatted;
    }
    return t('Routine');
  };

  const priorityBadgeUrgent = (study: SelectedStudy) =>
    Boolean(study.isCritical || priorityUsesUrgentPill(study.priority));

  return (
    <div className="-mt-2 font-sans text-[#000000]">
      <div className="border-b border-[#E0E0E0] px-6 pb-4 pr-14 pt-6">
        <h2 className="text-[22px] font-semibold leading-snug tracking-tight text-[#000000]">
          {isReassign ? t('Reassign Studies to Hub Facility') : t('Assign Studies to Hub Facility')}
        </h2>
        <p className="mt-1.5 text-sm leading-snug text-[#717171]">
          {isReassign
            ? t('Select a hub facility to reassign the selected studies for reporting')
            : t('Select a hub facility to assign the selected studies for reporting')}
        </p>
      </div>

      <div className="flex flex-col gap-6 px-6 py-6">
        <section>
          <h3 className="mb-3 text-sm font-semibold text-[#000000]">
            {t('Selected Studies')} ({studyCount})
          </h3>
          <div className="flex max-h-52 flex-col gap-2 overflow-y-auto pr-0.5">
            {studiesForDisplay.map(study => {
              const pid = study.patientId?.trim();
              const metaLine = pid ? `UHID-${pid}` : '';
              const urgent = priorityBadgeUrgent(study);
              return (
                <div
                  key={study.studyInstanceUid}
                  className="rounded-lg border border-[#E0E0E0] bg-white px-4 py-3"
                >
                  <div className="text-sm font-medium text-[#333333]">
                    {study.patientName?.trim() || t('Unknown patient')}
                  </div>
                  <div className="mt-0.5 text-sm font-normal text-[#666666]">
                    {study.studyDescription?.trim() ||
                      study.modalities?.trim() ||
                      t('Study description unavailable')}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-[#666666]">
                    {metaLine ? <span className="min-w-0 break-words">{metaLine}</span> : null}
                    <span
                      className={
                        urgent
                          ? 'inline-flex shrink-0 rounded-full bg-[#FEE2E2] px-2.5 py-0.5 text-[11px] font-medium uppercase tracking-wide text-[#B91C1C]'
                          : 'inline-flex shrink-0 rounded-full bg-[#F0F0F0] px-2.5 py-0.5 text-[11px] font-medium text-[#333333]'
                      }
                    >
                      {priorityBadgeLabel(study)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section className="flex flex-col gap-2">
          <label className="text-sm font-semibold text-[#000000]">{t('Select Hub Facility')}</label>
          <VirtualizedDropdown
            value={selectedHubId}
            onChange={handleHubChange}
            options={hubOptions}
            placeholder={t('Choose a hub facility...')}
            searchValue={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder={t('Search hub facilities...')}
            emptyMessage={t('No hub facilities found')}
            className="w-full"
            showNone={false}
            variant="light"
            truncateTriggerLabel={false}
          />
          <p className="text-xs leading-relaxed text-[#717171]">
            {t('Studies will appear in the selected hub facility worklist for reporting')}
          </p>
        </section>
      </div>

      <div className="flex flex-wrap justify-end gap-3 border-t border-[#E0E0E0] px-6 py-4">
        <Button
          variant="outline"
          onClick={onClose}
          className="min-w-[96px] rounded-md border-[#E0E0E0] bg-white px-4 py-2 text-[#000000] hover:bg-[#FAFAFA]"
          disabled={isAssigning}
        >
          {t('Cancel')}
        </Button>
        <Button
          variant="default"
          onClick={handleAssign}
          disabled={selectedHubId === null || isAssigning}
          className="min-w-[120px] rounded-md border-0 px-4 py-2 text-white hover:brightness-95 disabled:opacity-50 disabled:hover:brightness-100"
          style={{ backgroundColor: '#118ab2' }}
        >
          {isAssigning
            ? isReassign
              ? t('Reassigning...')
              : t('Assigning...')
            : isReassign
              ? t('Reassign to Hub Facility')
              : t('Assign to Hub Facility')}
        </Button>
      </div>
    </div>
  );
};

export default AssignHubFacilityModal;
